import React, { useState } from 'react';
import { X, Folder, Home, ChevronRight, Move } from 'lucide-react';
import { Folder as FolderType } from '../../types'; // Keep this import
import { useClickOutside } from '../../hooks/useClickOutside';
import { useScrollLock } from '../../hooks/useScrollLock';

interface MoveToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folders: FolderType[];
  selectedPromptIds: string[];
  onMovePrompts: (folderId: string | null) => void;
}

export const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  isOpen,
  onClose,
  folders,
  selectedPromptIds,
  onMovePrompts,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const modalRef = useClickOutside(() => {
    if (isOpen) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Debug folders data
  React.useEffect(() => {
    if (isOpen) {
      console.log('🔵 MoveToFolderModal: Modal opened with folders:', {
        totalFolders: folders.length,
        folders: folders.map(f => ({ id: f.id, name: f.name, parentId: f.parentId }))
      });
    }
  }, [isOpen, folders]);

  const handleMove = () => {
    onMovePrompts(selectedFolderId);
    onClose();
  };

  const buildFolderTree = (parentId?: string, level = 0): React.ReactNode[] => {
    const subfolders = folders.filter(f => {
      // For root level (parentId undefined), find folders with no parentId or null parentId
      if (parentId === undefined) {
        return !f.parentId || f.parentId === null;
      }
      // For nested levels, match exact parentId
      return f.parentId === parentId;
    });
    
    return subfolders.map(folder => (
      <div key={folder.id}>
        <button
          type="button"
          onClick={() => setSelectedFolderId(folder.id)}
          className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center ${
            selectedFolderId === folder.id // Keep this logic
              ? 'bg-fire-100 dark:bg-fire-900/30 text-fire-700 dark:text-fire-300'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
          style={{ paddingLeft: `${12 + level * 20}px` }}
        >
          <Folder 
            className="h-4 w-4 mr-2 flex-shrink-0" 
            style={{ color: folder.color }}
          />
          <span className="truncate">{folder.name}</span>
        </button>
        {buildFolderTree(folder.id, level + 1)}
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border"> {/* Keep this div */}
          <div className="flex items-center">
            <Move className="h-6 w-6 text-fire-600 dark:text-fire-400 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
                Move to Folder
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {selectedPromptIds.length} prompt{selectedPromptIds.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {/* Root folder option */}
            <button
              type="button"
              onClick={() => setSelectedFolderId(null)} // Keep this action
              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center ${
                selectedFolderId === null
                  ? 'bg-fire-100 dark:bg-fire-900/30 text-fire-700 dark:text-fire-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Home className="h-4 w-4 mr-2" />
              Root (No folder)
            </button>

            {/* Folder tree */}
            {buildFolderTree()}
          </div>

          {folders.length === 0 && (
            <div className="text-center py-8">
              <Folder className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                No folders available. Create a folder first.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleMove}
            disabled={selectedFolderId === undefined} // Keep this logic
            className="bg-fire-gradient text-white px-6 py-2 rounded-lg hover:shadow-fire-glow focus:outline-none focus:ring-2 focus:ring-fire-500 focus:ring-offset-2 transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            <Move className="h-4 w-4 mr-2" />
            Move Here
          </button>
        </div>
      </div>
    </div>
  );
};